// Configuración dinámica de la URL de la API
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.origin.includes('localhost') ? 'http://localhost:8080/api' : `${window.location.origin}/api`);
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (window.location.origin.includes('localhost') ? 'http://localhost:8080/api' : `${window.location.origin}/api`);

class AuthService {
  constructor() {
    this.token = null;
  }

  // Configurar token para todas las peticiones
  setAuthToken(token) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  // Remover token y limpiar datos de autenticación
  removeAuthToken() {
    this.token = null;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  // Obtener token del localStorage
  getToken() {
    if (!this.token) {
      this.token = localStorage.getItem('token');
    }
    return this.token;
  }

  // Obtener datos del usuario
  getUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  // Verificar si el usuario está autenticado
  isAuthenticated() {
    const token = this.getToken();
    const user = this.getUser();

    // Si no hay token o usuario, no está autenticado
    if (!token || !user) {
      return false;
    }

    try {
      // Verificar si el token está expirado
      const payload = JSON.parse(atob(token.split('.')[1]));
      const isTokenValid = payload.exp * 1000 > Date.now();

      // Verificar que el token y el usuario coincidan
      const isUserValid = user && user.email;

      // Si el token es inválido o el usuario no es válido, limpiar datos
      if (!isTokenValid || !isUserValid) {
        this.removeAuthToken();
        return false;
      }

      return true;
    } catch (e) {
      console.error('Error al verificar autenticación:', e);
      this.removeAuthToken();
      return false;
    }
  }

  // Guardar token y datos del usuario
  setAuthData(token, userData) {
    this.setAuthToken(token);
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
    }
  }

  // Eliminar datos de autenticación
  clearAuthData() {
    this.removeAuthToken();
    localStorage.removeItem('user');
  }

  // Headers para peticiones autenticadas
  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // Hacer petición autenticada
  async authenticatedRequest(url, options = {}) {
    const token = this.getToken();
    if (!token) {
      throw new Error('No hay token de autenticación');
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (response.status === 401) {
      this.clearAuthData();
      window.location.href = '/login';
      throw new Error('Sesión expirada');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Error en la petición');
    }

    return response;
  }

  // Registro de usuario
  async register(userData) {
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al registrar usuario');
      }

      // No se guarda token: el usuario debe verificar su email primero
      return data;
    } catch (error) {
      console.error('Error en registro:', error);
      throw error;
    }
  }

  // Verificar email con token
  async verifyEmail(token) {
    try {
      const response = await fetch(`${API_URL}/auth/verify-email?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al verificar email');
      }

      return data;
    } catch (error) {
      console.error('Error verificando email:', error);
      throw error;
    }
  }

  // Reenviar correo de verificación
  async resendVerification(email) {
    try {
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al reenviar verificación');
      }

      return data;
    } catch (error) {
      console.error('Error reenviando verificación:', error);
      throw error;
    }
  }

  // Login con Google
  async googleLogin(credential) {
    try {
      const response = await fetch(`${API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al iniciar sesión con Google');
      }

      if (!data.token) {
        throw new Error('No se recibió token de autenticación');
      }

      this.setAuthData(data.token, data.user || { email: data.email });

      return {
        token: data.token,
        user: data.user || { email: data.email },
        ...data,
      };
    } catch (error) {
      console.error('Error en login con Google:', error);
      throw error;
    }
  }

  // Login de usuario
  async login(email, password) {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        const err = new Error(data.message || data.error || 'Error al iniciar sesión');
        if (data.requires_verification) {
          err.requiresVerification = true;
          err.verificationEmail = data.email;
        }
        throw err;
      }

      if (!data.token) {
        throw new Error('No se recibió token de autenticación');
      }

      // Configurar token y datos de usuario después del login
      this.setAuthData(data.token, data.user || { email });

      return {
        token: data.token,
        user: data.user || { email },
        ...data,
      };
    } catch (error) {
      console.error('Error en login:', error);
      throw error;
    }
  }

  // Obtener perfil del usuario
  async getProfile() {
    try {
      const response = await this.authenticatedRequest(`${API_BASE_URL}/profile`);
      const data = await response.json();

      // Actualizar datos del usuario si es necesario
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      return data;
    } catch (error) {
      console.error('Error al obtener perfil:', error);
      throw error;
    }
  }

  // Actualizar perfil del usuario
  async updateProfile(profileData) {
    try {
      const response = await this.authenticatedRequest(`${API_BASE_URL}/profile`, {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });
      const data = await response.json();
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
      }
      return data;
    } catch (error) {
      console.error('Error al actualizar perfil:', error);
      throw error;
    }
  }

  // Cambiar contraseña
  async changePassword(currentPassword, newPassword) {
    try {
      const response = await this.authenticatedRequest(`${API_BASE_URL}/profile/password`, {
        method: 'POST',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      return await response.json();
    } catch (error) {
      console.error('Error al cambiar contraseña:', error);
      throw error;
    }
  }

  // Renovar token
  async refreshToken() {
    try {
      const response = await this.authenticatedRequest(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al renovar token');
      }

      if (data.token) {
        const currentUser = this.getUser();
        this.setAuthData(data.token, currentUser);
      }

      return data;
    } catch (error) {
      console.error('Error al renovar token:', error);
      throw error;
    }
  }

  // Suscripción (endpoint público)
  async subscribe(email) {
    try {
      const response = await fetch(`${API_URL}/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || 'Error en la suscripción');
      }

      return await response.json();
    } catch (error) {
      console.error('Error en suscripción:', error);
      throw error;
    }
  }

  // Verificar si el token está válido
  async verifyToken() {
    return this.isAuthenticated();
  }

  // Logout (limpiar datos locales)
  logout() {
    this.clearAuthData();
  }

  // Obtener lista de usuarios
  async getUsers() {
    try {
      const response = await this.authenticatedRequest(`${API_BASE_URL}/users`);
      return await response.json();
    } catch (error) {
      console.error('Error al obtener usuarios:', error);
      throw error;
    }
  }
}

// Crear una instancia única del servicio
const authService = new AuthService();

export default authService;
